import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ENV } from '@environment';
import GoogleMapReact from 'google-map-react';
import { MyThunkDispatch } from 'store';

import { addressesRequest, addressesSuccessful } from 'action-creators/addresses';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { Airport } from 'models/airport';
import { VehiclePlace } from 'models/auto/vehicleSearch';
import { City } from 'models/city';
import { getCity, getCountry, getCountryCode, getState, GoogleMapsWrapper } from 'utils/googlemaps';

import { MapBox } from './style';

const GOOGLE_MAPS_API_KEY = `${ENV.API_KEYS.GOOGLE_MAPS_API_KEY}`;
const TIMEOUT_SEARCH = 1000;
let gmapsWrapper: GoogleMapsWrapper;

interface Props {
  children?: JSX.Element;
  query: string;
  onStart: () => void;
  onEnd: (results: google.maps.places.AutocompletePrediction[]) => void;
  onSelected: (place: Airport | City | VehiclePlace) => void;
  selected?: Airport | City | VehiclePlace;
}
const GooglePlacesSearch = ({ children, query, onStart, onEnd, onSelected, selected }: Props) => {
  const { t } = useStoreTranslation();
  const dispatch: MyThunkDispatch = useDispatch();

  const [gMapsReady, setGMapsReady] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<number>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selection, setSelection] = useState<City | Airport | VehiclePlace | undefined>();

  const doSearch = useCallback(
    async (value) => {
      setKeyword(value);
      onStart();
      setIsLoading(true);
      dispatch(addressesRequest());
      try {
        const r = await gmapsWrapper.autocompleteSearch(value);
        dispatch(addressesSuccessful(value, []));
        onEnd(r);
      } catch (error) {
        onEnd([]);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, onEnd, onStart],
  );

  const loadedGoogleApi = useCallback((mapObj, mapsObj) => {
    setGMapsReady(true);
    gmapsWrapper = new GoogleMapsWrapper(mapObj, mapsObj);
  }, []);

  useEffect(() => {
    const handleSearch = (value: string) => {
      if (gMapsReady && value.trim().length > 2 && value !== keyword) {
        let timeout = searchTimeout;
        clearTimeout(timeout);
        timeout = window.setTimeout(async () => {
          doSearch(value);
        }, TIMEOUT_SEARCH);
        setSearchTimeout(timeout);
      }
    };

    if (!isLoading && query !== keyword) {
      handleSearch(query);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    const fillFields = async (selected, placeId) => {
      if (gmapsWrapper) {
        const res = await gmapsWrapper.geocodePlaceId(placeId);
        const addr = res && res.length > 0 && res[0] && res[0].address_components ? res[0].address_components : '';
        const formatted_address =
          res && res.length > 0 && res[0] && res[0].formatted_address ? res[0].formatted_address : '';
        const location =
          res && res.length > 0 && res[0] && res[0].geometry && res[0].geometry.location
            ? res[0].geometry.location
            : null;

        if (addr !== '' && formatted_address !== '') {
          setSelection({
            ...selected,
            latitude: location ? location.lat() : 0,
            longitude: location ? location.lng() : 0,
            code: getCity(addr),
            state: getState(addr),
            city: getCity(addr),
            country: getCountry(addr),
            countryCode: getCountryCode(addr),
            related_city: formatted_address,
            isAirport: false,
          });
        }
      }
    };
    if (selected && !selection) {
      if ('placeId' in selected) {
        const placeId = selected.placeId;
        if (placeId) {
          fillFields(selected, placeId);
        } else {
          setSelection(selected);
        }
      } else {
        setSelection(selected);
      }
    }
  }, [onSelected, selected, selection]);

  useEffect(() => {
    if (selection) {
      onSelected(selection);
      setSelection(undefined);
    }
  }, [onSelected, selection]);

  return (
    <div>
      {children}
      <MapBox>
        <GoogleMapReact
          bootstrapURLKeys={{
            key: GOOGLE_MAPS_API_KEY,
            language: t('locale').substring(0, 2),
            region: t('locale').substring(3),
            libraries: ['places'],
          }}
          defaultCenter={{ lat: 0, lng: 0 }}
          defaultZoom={10}
          yesIWantToUseGoogleMapApiInternals={true}
          options={{
            fullscreenControl: false,
            zoomControl: false,
          }}
          onGoogleApiLoaded={({ map, maps }) => loadedGoogleApi(map, maps)}
        />
      </MapBox>
    </div>
  );
};

export default GooglePlacesSearch;
